import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

interface FormData {
  email: string;
  type: 'user' | 'merchant' | 'enterprise';
  ageConfirm: boolean;
}

const InnovationPage: React.FC = () => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    type: 'user',
    ageConfirm: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  React.useEffect(() => {
    document.title = 'Innovation & Patents — Earn-Time Token Commitment | Rewardly';

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('article:')) {
          el.setAttribute('property', name);
        } else {
          el.setAttribute('name', name);
        }
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', '9 patent families protecting earn-time blockchain token commitment. 3 filings across US and 150+ countries. The first loyalty protocol with patent-pending IP.');
    setMeta('og:title', '9 Patent Families Protecting Earn-Time Loyalty Innovation');
    setMeta('og:description', 'The first patent filings for earn-time blockchain token commitment in loyalty programs. US and international.');
    setMeta('og:type', 'website');
    setMeta('og:url', 'https://rewardly.live/innovation');
    setMeta('og:image', 'https://rewardly.live/og-image.png');
    setMeta('og:site_name', 'Rewardly Protocol');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:site', '@Rewardly_io');
    setMeta('twitter:title', '9 Patent Families Protecting Earn-Time Loyalty Innovation');
    setMeta('twitter:description', 'The first patent filings for earn-time blockchain token commitment in loyalty programs. US and international.');
    setMeta('twitter:image', 'https://rewardly.live/og-image.png');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://rewardly.live/innovation');
  }, []);

  React.useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [modalOpen]);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );
    const revealElements = document.querySelectorAll('.reveal');
    revealElements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const validateEmail = (email: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!validateEmail(formData.email)) { setSubmitMessage('Please enter a valid email address'); return; }
    if (!formData.ageConfirm) { setSubmitMessage('Please confirm you are 18 or older'); return; }
    setIsSubmitting(true);
    setSubmitMessage('');
    try {
      const response = await fetch('https://5g72iuzmj9.execute-api.us-east-1.amazonaws.com/dev/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, user_type: formData.type })
      });
      const raw = await response.json();
      let data: any;
      if (raw.body && typeof raw.body === 'string') {
        try { data = JSON.parse(raw.body); } catch { data = raw; }
      } else { data = raw; }
      const statusCode = raw.statusCode || response.status;
      if (statusCode === 200 && !data.error) {
        setSubmitMessage(data.new_subscription
          ? "Welcome! You're on the list. Check your email for confirmation."
          : "You're already on the waitlist! We'll email you when early access opens.");
        setFormData({ email: '', type: 'user', ageConfirm: false });
        setTimeout(() => { setModalOpen(false); setSubmitMessage(''); }, 3000);
      } else {
        setSubmitMessage(data.error || 'Failed to join waitlist. Please try again.');
      }
    } catch {
      setSubmitMessage('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const target = e.target as HTMLInputElement;
    const { name, value, type, checked } = target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (submitMessage) setSubmitMessage('');
  };

  const families = [
    { n: 1, name: 'Earn-Time Token Commitment', desc: 'Value allocated at earning, not redemption — oracle-priced, immutable, no expiration', status: 'Filed (PPA + PCT)', filed: true },
    { n: 2, name: 'AI Receipt Validation Oracle', desc: 'ML-validated purchase proof triggers blockchain token allocation without POS integration', status: 'Filed (PPA + PCT)', filed: true },
    { n: 3, name: 'AI Coalition Promotion Engine', desc: 'AI optimizes promotional multipliers within on-chain governance constraints', status: 'In Pipeline', filed: false },
    { n: 4, name: 'Bilateral Obligation Settlement', desc: 'Unified health factor across consumer commitments and enterprise USDC escrow', status: 'In Pipeline', filed: false },
    { n: 5, name: 'Pool-Health-Gated Conversion', desc: 'Real-time reserve health gates redemption paths differentially', status: 'In Pipeline', filed: false },
    { n: 6, name: 'Coalition Tier Escrow Protocol', desc: 'On-chain multi-tier escrow with automated enterprise settlement', status: 'In Pipeline', filed: false },
    { n: 7, name: 'Demand-Driven Yield-Gap Staking', desc: 'Emission throttles to zero when protocol fees cover staking yield', status: 'In Pipeline', filed: false },
    { n: 8, name: '5-Path Redemption Architecture', desc: 'Unified routing across five heterogeneous settlement paths', status: 'In Pipeline', filed: false },
    { n: 9, name: 'Dual-Adapter Cross-Chain Bridge', desc: 'LayerZero + Wormhole orchestration with unified supply invariant', status: 'In Pipeline', filed: false },
  ];

  const moat = [
    { title: 'Technical', icon: '⬡', desc: '7-layer architecture, 38+ contracts, AI validation pipeline. 2–3 years minimum to replicate.' },
    { title: 'Legal', icon: '⚖', desc: '9 patent families. US + PCT international. Trademarks in multiple jurisdictions. Copyright on all code.' },
    { title: 'Network', icon: '◎', desc: 'Coalition escrow creates lock-in. Earn-time commitment history is on-chain — non-portable to competitors.' },
    { title: 'Data', icon: '◈', desc: 'Every receipt validated improves the AI. Every campaign result trains optimization. Data moat compounds with scale.' },
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Crimson+Text:ital,wght@0,400;0,600;1,400&family=Outfit:wght@400;500;600;700;800&display=swap');

        :root {
          --white: #FFFFFF;
          --off-white: #F8FAFC;
          --gray-100: #F1F5F9;
          --gray-200: #E2E8F0;
          --gray-400: #94A3B8;
          --gray-500: #64748B;
          --gray-700: #374151;
          --gray-900: #0F172A;
          --blue-600: #2563EB;
          --blue-700: #1D4ED8;
          --gold: #D4A853;
          --gold-dark: #B8923A;
          --green-600: #059669;
        }

        * { margin: 0; padding: 0; box-sizing: border-box; }
        html { scroll-behavior: smooth; overflow-x: hidden; }
        body {
          font-family: 'Inter', -apple-system, sans-serif;
          background: var(--white);
          color: var(--gray-900);
          line-height: 1.6;
          -webkit-font-smoothing: antialiased;
          overflow-x: hidden;
        }

        h1, h2, h3, .section-title, .stat-value, .inversion-title {
          font-family: 'Outfit', 'Inter', -apple-system, sans-serif;
        }

        .container { max-width: 1140px; margin: 0 auto; padding: 0 24px; }
        .container-narrow { max-width: 800px; margin: 0 auto; padding: 0 24px; }

        /* Modal */
        .modal-overlay { display: none; position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 2000; align-items: center; justify-content: center; }
        .modal-overlay.active { display: flex; }
        .modal { background: var(--white); border-radius: 16px; padding: 32px; width: 100%; max-width: 440px; margin: 24px; box-shadow: 0 20px 60px rgba(0,0,0,0.2); }
        .modal h3 { font-size: 24px; font-weight: 700; margin-bottom: 24px; }
        .modal-field { margin-bottom: 20px; }
        .modal-field label { display: block; font-size: 14px; font-weight: 500; color: var(--gray-500); margin-bottom: 8px; }
        .modal-field input, .modal-field select { width: 100%; padding: 14px 16px; border: 1px solid var(--gray-200); border-radius: 8px; font-size: 15px; font-family: inherit; transition: border-color 0.2s; }
        .modal-field input:focus, .modal-field select:focus { outline: none; border-color: var(--blue-600); }
        .modal-checkbox { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 24px; }
        .modal-checkbox input { width: 18px; height: 18px; margin-top: 2px; accent-color: var(--blue-600); }
        .modal-checkbox span { font-size: 14px; color: var(--gray-500); line-height: 1.5; }
        .modal-buttons { display: flex; gap: 12px; }
        .modal-buttons .btn { flex: 1; justify-content: center; padding: 14px 20px; }
        .submit-message { padding: 12px; border-radius: 8px; margin-bottom: 20px; font-size: 14px; }
        .submit-message.success { background: #DEF7EC; color: #03543F; }
        .submit-message.error { background: #FDE8E8; color: #9B1C1C; }

        /* Buttons */
        .btn { display: inline-flex; align-items: center; gap: 8px; padding: 10px 20px; border-radius: 8px; font-weight: 600; font-size: 14px; text-decoration: none; transition: all 0.2s; border: none; cursor: pointer; }
        .btn-primary { background: var(--blue-600); color: var(--white); }
        .btn-primary:hover { background: var(--blue-700); transform: translateY(-1px); }
        .btn-outline { background: transparent; color: var(--white); border: 1px solid rgba(255,255,255,0.4); }
        .btn-outline:hover { border-color: var(--white); background: rgba(255,255,255,0.08); }
        .btn-gold { background: var(--gold); color: var(--gray-900); }
        .btn-gold:hover { background: var(--gold-dark); transform: translateY(-1px); }

        /* Hero */
        .hero {
          padding: 140px 0 80px;
          background: var(--gray-900);
          color: var(--white);
          text-align: center;
        }
        .hero-label {
          display: inline-block;
          padding: 6px 16px;
          background: rgba(212,168,83,0.15);
          color: var(--gold);
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 24px;
        }
        .hero h1 {
          font-size: clamp(36px, 5vw, 52px);
          font-weight: 800;
          line-height: 1.15;
          margin-bottom: 16px;
          color: var(--white);
        }
        .hero-subtitle {
          font-family: 'Crimson Text', serif;
          font-size: 22px;
          font-style: italic;
          color: rgba(255,255,255,0.7);
          max-width: 640px;
          margin: 0 auto 40px;
          line-height: 1.6;
          letter-spacing: 0.3px;
        }
        .hero-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          max-width: 600px;
          margin: 0 auto;
        }
        .hero-stat-value {
          font-size: 36px;
          font-weight: 800;
          color: var(--gold);
        }
        .hero-stat-label {
          font-size: 14px;
          color: rgba(255,255,255,0.6);
          margin-top: 4px;
        }

        /* Section common */
        .section-label {
          display: inline-block;
          background: var(--blue-600);
          color: var(--white);
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 16px;
        }
        .section-title {
          font-size: clamp(26px, 4vw, 36px);
          font-weight: 800;
          margin-bottom: 16px;
          color: var(--gray-900);
        }
        .section-subtitle {
          font-size: 17px;
          color: var(--gray-500);
          max-width: 640px;
          margin: 0 auto 48px;
          line-height: 1.6;
        }
        .section-header { text-align: center; margin-bottom: 48px; }

        /* Core Invention */
        .invention-section { padding: 80px 0; background: var(--white); }
        .invention-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 48px;
          align-items: start;
          max-width: 1000px;
          margin: 0 auto;
        }
        .invention-prose p {
          font-size: 17px;
          line-height: 1.8;
          color: var(--gray-700);
          margin-bottom: 20px;
        }
        .invention-prose p:last-child { margin-bottom: 0; }
        .invention-prose p strong { color: var(--gray-900); }
        .filing-card {
          background: var(--off-white);
          border: 1px solid var(--gray-200);
          border-radius: 12px;
          padding: 32px;
        }
        .filing-card-title {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: var(--gold-dark);
          margin-bottom: 20px;
        }
        .filing-item {
          padding: 14px 0;
          border-bottom: 1px solid var(--gray-200);
        }
        .filing-item:last-of-type { border-bottom: none; }
        .filing-item-number {
          font-size: 12px;
          font-weight: 700;
          color: var(--blue-600);
          margin-bottom: 2px;
        }
        .filing-item-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--gray-900);
          margin-bottom: 2px;
        }
        .filing-item-date {
          font-size: 13px;
          color: var(--gray-500);
        }
        .filing-applicant {
          margin-top: 16px;
          font-size: 13px;
          color: var(--gray-500);
        }
        .filing-applicant strong { color: var(--gray-900); }

        /* 9 Families */
        .families-section { padding: 80px 0; background: var(--off-white); }
        .families-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }
        .family-card {
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: 12px;
          padding: 24px;
          transition: box-shadow 0.2s;
        }
        .family-card:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.08); }
        .family-number {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: var(--gray-400);
          margin-bottom: 8px;
        }
        .family-name {
          font-size: 16px;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 8px;
          line-height: 1.3;
        }
        .family-desc {
          font-size: 13px;
          color: var(--gray-500);
          line-height: 1.6;
          margin-bottom: 16px;
        }
        .status-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .status-filed {
          background: rgba(212,168,83,0.1);
          border: 1px solid rgba(212,168,83,0.25);
          color: var(--gold-dark);
        }
        .status-pipeline {
          background: rgba(37,99,235,0.08);
          border: 1px solid rgba(37,99,235,0.15);
          color: var(--blue-600);
        }

        /* Four-Layer Moat */
        .moat-section { padding: 80px 0; background: var(--white); }
        .moat-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
        }
        .moat-card {
          background: var(--off-white);
          border: 1px solid var(--gray-200);
          border-radius: 12px;
          padding: 28px 24px;
        }
        .moat-icon {
          font-size: 24px;
          margin-bottom: 12px;
          display: block;
        }
        .moat-title {
          font-size: 16px;
          font-weight: 700;
          color: var(--gray-900);
          margin-bottom: 10px;
        }
        .moat-desc {
          font-size: 14px;
          color: var(--gray-500);
          line-height: 1.6;
        }

        /* Pull Quote */
        .quote-section {
          padding: 80px 0;
          background: var(--off-white);
          text-align: center;
        }
        .pull-quote {
          font-family: 'Crimson Text', serif;
          font-size: clamp(22px, 3.5vw, 32px);
          font-style: italic;
          color: var(--gray-900);
          max-width: 700px;
          margin: 0 auto 16px;
          line-height: 1.4;
        }
        .pull-attribution {
          font-size: 14px;
          color: var(--gray-500);
          margin-bottom: 20px;
        }
        .pull-link {
          font-size: 15px;
          font-weight: 600;
          color: var(--blue-600);
          text-decoration: none;
        }
        .pull-link:hover { text-decoration: underline; }

        /* CTA */
        .cta-section {
          padding: 80px 0;
          background: linear-gradient(135deg, var(--blue-600) 0%, var(--blue-700) 100%);
          text-align: center;
        }
        .cta-section h2 {
          font-size: clamp(28px, 4vw, 36px);
          font-weight: 800;
          color: var(--white);
          margin-bottom: 16px;
        }
        .cta-section p {
          font-size: 18px;
          color: rgba(255,255,255,0.85);
          margin-bottom: 32px;
          max-width: 560px;
          margin-left: auto;
          margin-right: auto;
          line-height: 1.6;
        }
        .cta-buttons {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        /* Footer helpers */
        .footer-content { display: grid; grid-template-columns: 2fr repeat(3, 1fr); gap: 40px; }
        .footer-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-top: 32px; padding-top: 32px; border-top: 1px solid rgba(255,255,255,0.1); text-align: center; font-size: 14px; }

        /* Scroll reveal animations */
        .reveal {
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.6s ease-out, transform 0.6s ease-out;
        }
        .reveal.visible {
          opacity: 1;
          transform: translateY(0);
        }

        @media (prefers-reduced-motion: reduce) {
          .reveal { opacity: 1; transform: none; transition: none; }
        }

        @media (max-width: 768px) {
          .nav-menu, .nav-actions { display: none; }
          .mobile-toggle { display: flex; }

          .hero { padding: 100px 0 48px; }
          .hero h1 { font-size: 32px; }
          .hero-subtitle { font-size: 18px; }
          .hero-stats { grid-template-columns: repeat(3, 1fr); gap: 16px; }
          .hero-stat-value { font-size: 26px; }

          .invention-grid { grid-template-columns: 1fr; gap: 32px; }

          .families-grid { grid-template-columns: 1fr; }

          .moat-grid { grid-template-columns: repeat(2, 1fr); }

          .cta-section p { font-size: 16px; }
          .cta-buttons .btn { width: 100%; justify-content: center; }
          .cta-buttons { flex-direction: column; align-items: stretch; max-width: 320px; margin: 0 auto; }

          .footer-content { grid-template-columns: 1fr; }
          .footer-grid { grid-template-columns: repeat(2, 1fr); }

          .modal-buttons { flex-direction: column; }
          .reveal {
            transition-delay: 0s !important;
            transition-duration: 0.35s;
          }
        }

        @media (max-width: 480px) {
          .hero-stats { grid-template-columns: 1fr; gap: 20px; }
          .moat-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* Modal */}
      <div
        className={`modal-overlay ${modalOpen ? 'active' : ''}`}
        onClick={() => setModalOpen(false)}
      >
        <div className="modal" onClick={e => e.stopPropagation()}>
          <h3>Join the Waitlist</h3>
          {submitMessage && (
            <div className={`submit-message ${submitMessage.includes('Welcome') || submitMessage.includes('already') ? 'success' : 'error'}`}>
              {submitMessage}
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="modal-field">
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
            </div>
            <div className="modal-field">
              <label>I am a...</label>
              <select name="type" value={formData.type} onChange={handleInputChange} required disabled={isSubmitting}>
                <option value="user">User</option>
                <option value="merchant">Merchant</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div className="modal-checkbox">
              <input type="checkbox" name="ageConfirm" checked={formData.ageConfirm} onChange={handleInputChange} disabled={isSubmitting} />
              <span>I confirm I am 18 years of age or older</span>
            </div>
            <div className="modal-buttons">
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Join Waitlist'}
              </button>
              <button type="button" className="btn btn-outline" style={{color: 'var(--gray-900)', borderColor: 'var(--gray-200)'}} onClick={() => setModalOpen(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      <Header onJoinWaitlist={() => setModalOpen(true)} />

      {/* Hero */}
      <section className="hero">
        <div className="container">
          <div className="hero-label">Intellectual Property · Patent Pending</div>
          <h1>Protected by Real Innovation</h1>
          <p className="hero-subtitle">
            The first patent filings for earn-time blockchain token commitment in loyalty programs — filed in the US and internationally across 150+ countries.
          </p>
          <div className="hero-stats">
            <div>
              <div className="hero-stat-value">3</div>
              <div className="hero-stat-label">Filings</div>
            </div>
            <div>
              <div className="hero-stat-value">150+</div>
              <div className="hero-stat-label">Countries</div>
            </div>
            <div>
              <div className="hero-stat-value">9</div>
              <div className="hero-stat-label">Patent Families</div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Invention */}
      <section className="invention-section">
        <div className="container">
          <div style={{textAlign: 'center', marginBottom: '48px'}}>
            <div className="section-label">The Breakthrough</div>
            <h2 className="section-title">Value Guaranteed at the Moment You Earn It</h2>
          </div>
          <div className="invention-grid">
            <div className="invention-prose reveal">
              <p>
                Every loyalty program ever built — airlines, hotels, retail, banking — allocates value at the moment of redemption. The customer earns a vague promise and discovers what it's worth only when they try to use it. This is why <strong>nearly half of the $300 billion in loyalty value issued each year goes unredeemed.</strong>
              </p>
              <p>
                Rewardly inverts this completely. Value is allocated — committed, on-chain, guaranteed — at the moment of earning. The customer shops, the AI validates the receipt, and in that instant, RWLY tokens are committed at that day's oracle price. The quantity is fixed. It cannot be expired, devalued, or revoked. It is theirs.
              </p>
              <p>
                No loyalty program, blockchain or otherwise, has ever made this promise and been architecturally capable of keeping it.
              </p>
            </div>
            <div className="filing-card reveal" style={{transitionDelay: '0.15s'}}>
              <div className="filing-card-title">Patent Filings (Public Record)</div>
              <div className="filing-item">
                <div className="filing-item-number">US Provisional Patent Application</div>
                <div className="filing-item-title">First Filing</div>
                <div className="filing-item-date">Filed March 2026</div>
              </div>
              <div className="filing-item">
                <div className="filing-item-number">US Provisional Patent Application</div>
                <div className="filing-item-title">Second Filing</div>
                <div className="filing-item-date">Filed March 2026</div>
              </div>
              <div className="filing-item">
                <div className="filing-item-number">PCT International Application</div>
                <div className="filing-item-title">International Coverage</div>
                <div className="filing-item-date">Filed March 2026 · 150+ countries</div>
              </div>
              <div className="filing-applicant">
                <strong>Filed by:</strong> Rewardly Protocol, Inc.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nine Patent Families */}
      <section className="families-section">
        <div className="container">
          <div className="section-header">
            <div className="section-label">Patent Portfolio</div>
            <h2 className="section-title">Nine Families of Innovation</h2>
            <p className="section-subtitle">From the core earn-time mechanism to AI validation, coalition settlement, cross-chain bridging, and beyond.</p>
          </div>
          <div className="families-grid">
            {families.map((f, i) => (
              <div key={f.n} className="family-card reveal" style={{transitionDelay: `${i * 0.05}s`}}>
                <div className="family-number">Family {f.n}</div>
                <div className="family-name">{f.name}</div>
                <div className="family-desc">{f.desc}</div>
                <span className={`status-badge ${f.filed ? 'status-filed' : 'status-pipeline'}`}>
                  {f.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Four-Layer Moat */}
      <section className="moat-section">
        <div className="container">
          <div className="section-header">
            <div className="section-label">Defensibility</div>
            <h2 className="section-title">Four Layers of Protection</h2>
          </div>
          <div className="moat-grid">
            {moat.map((m, i) => (
              <div key={m.title} className="moat-card reveal" style={{transitionDelay: `${i * 0.08}s`}}>
                <span className="moat-icon">{m.icon}</span>
                <div className="moat-title">{m.title}</div>
                <div className="moat-desc">{m.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Manifesto Pull Quote */}
      <section className="quote-section">
        <div className="container">
          <div className="pull-quote">
            "The world doesn't need another app. It needs architecture for trust."
          </div>
          <div className="pull-attribution">— The Silent Manifesto</div>
          <a href="/manifesto" className="pull-link">Read the full manifesto →</a>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <h2>Interested in the Technology?</h2>
          <p>Read the whitepaper for the complete technical blueprint, or reach out to discuss partnership.</p>
          <div className="cta-buttons">
            <a href="/whitepaper" className="btn btn-gold" style={{padding: '14px 28px', fontSize: '15px'}}>Read the Whitepaper →</a>
            <a href="mailto:info@rewardly.live" className="btn btn-outline" style={{padding: '14px 28px', fontSize: '15px'}}>Talk to the Team →</a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InnovationPage;
